/**
 * What a row will actually do, once the person has had their say.
 *
 * Three things can decide a row: the verdict the plan arrived with, an answer
 * given for its whole class, and an answer given for that row alone. The order
 * between them is not a detail - it is the difference between "all the repeats,
 * except this one" being sayable and not.
 *
 * These are the rules A's acceptance specs pin from the other side
 * (OneAnswerCanCoverEveryRepeatInTheFile, ARowAnsweredOnItsOwnBeatsTheAnswerForAll),
 * kept here as plain functions so the screen cannot quietly grow a fourth rule
 * of its own.
 */
import { PlanAction, PlanRow } from '../../services/table-import-export.service';

export interface RowDecisions {
  /** Row number to action. Beats everything. */
  rowActions: Record<number, PlanAction>;
  /** Reason code to action, covering the rows nobody scrolled to. */
  reasonActions: Record<string, PlanAction>;
}

export const NO_DECISIONS: RowDecisions = { rowActions: {}, reasonActions: {} };

/**
 * Blocked is not a preference. The row is invalid, so no answer from anyone
 * turns it into a write - and this is the single place that is enforced, so
 * the screen cannot offer a control that the rules would not honour.
 */
export function canBeWritten(row: PlanRow): boolean {
  return row.action !== 'Blocked';
}

/**
 * Which write a held-back row becomes when someone asks for it to go in.
 *
 * Not a question to put to a person: whether this is a new record or an
 * existing one is a fact about the data, not a preference. The plan already
 * knows - it named a target or it did not.
 */
export function writeActionFor(row: PlanRow): PlanAction {
  return row.targetId === null ? 'Create' : 'Update';
}

export function effectiveAction(
  row: PlanRow,
  decisions: RowDecisions,
): PlanAction {
  if (!canBeWritten(row)) return 'Blocked';

  const forThisRow = decisions.rowActions[row.rowNo];
  if (forThisRow !== undefined) return honour(row, forThisRow);

  const forItsClass = row.reason === null
    ? undefined
    : decisions.reasonActions[row.reason];
  if (forItsClass !== undefined) return honour(row, forItsClass);

  return row.action;
}

/** The counts, recomputed from the answers, so the headline moves with them. */
export function countActions(
  rows: readonly PlanRow[],
  decisions: RowDecisions,
): Record<PlanAction, number> {
  const counts: Record<PlanAction, number> =
    { Create: 0, Update: 0, Skip: 0, Blocked: 0 };
  for (const row of rows) counts[effectiveAction(row, decisions)]++;
  return counts;
}

export interface ReasonGroup {
  reason: string;
  rows: PlanRow[];
}

/**
 * Rows of one verdict, gathered by why. File order within a group, so a person
 * checking against their spreadsheet reads them in the order they wrote them.
 */
export function groupByReason(rows: readonly PlanRow[]): ReasonGroup[] {
  const groups = new Map<string, PlanRow[]>();
  for (const row of rows) {
    const reason = row.reason ?? 'Unspecified';
    const group = groups.get(reason);
    if (group) group.push(row);
    else groups.set(reason, [row]);
  }
  return [...groups].map(([reason, rowsInGroup]) =>
    ({ reason, rows: rowsInGroup }));
}

/**
 * Whether a whole class has been answered, and still is - one row answered on
 * its own does not undo the class answer, so the control has to keep showing
 * what it set.
 */
export function classAnswer(
  reason: string,
  decisions: RowDecisions,
): PlanAction | null {
  return decisions.reasonActions[reason] ?? null;
}

function honour(row: PlanRow, wanted: PlanAction): PlanAction {
  if (wanted === 'Skip') return 'Skip';
  if (wanted === 'Blocked') return row.action;
  // Create and Update both mean "write it"; which one it is was never the
  // person's to decide, so an answer of either resolves against the data.
  return writeActionFor(row);
}
